import React, { useState, useEffect, useRef } from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

interface TikTokPlayerProps {
  videoId: string;
  videoUrl?: string;
  isActive: boolean;
  shouldLoad: boolean;
  poster?: string;
}

export default function TikTokPlayer({ videoId, videoUrl, isActive, shouldLoad, poster }: TikTokPlayerProps) {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isIframeEnabled, setIsIframeEnabled] = useState(shouldLoad);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [playbackStalled, setPlaybackStalled] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // Sync iframe mounting with shouldLoad
  useEffect(() => {
    setIsIframeEnabled(shouldLoad);
    if (!shouldLoad) {
      setIsReady(false);
      setIsPlaying(false);
      setPlaybackStalled(false);
      setCurrentTime(0);
    }
  }, [shouldLoad]);

  // Actual mute state: mute if inactive or if user toggled mute
  const isActuallyMuted = !isActive || isMuted;

  const sendPostMessage = (type: string, value?: any) => {
    if (iframeRef.current?.contentWindow) {
      const msg: Record<string, any> = {
        'x-tiktok-player': true,
        type
      };
      if (value !== undefined) msg.value = value;
      iframeRef.current.contentWindow.postMessage(msg, '*');
    }
  };

  // Sync play / pause on isActive changes
  useEffect(() => {
    if (isIframeEnabled) {
      if (isActive) {
        sendPostMessage('play');
        setIsPlaying(true);
      } else {
        sendPostMessage('pause');
        setIsPlaying(false);
      }
    }
  }, [isActive, isIframeEnabled]);

  // Sync mute state
  useEffect(() => {
    if (isIframeEnabled) {
      sendPostMessage(isActuallyMuted ? 'mute' : 'unMute');
    }
  }, [isIframeEnabled, isActuallyMuted]);

  // Listen for messages from TikTok player
  useEffect(() => {
    if (!isIframeEnabled) return;

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('tiktok.com')) return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data && data['x-tiktok-player']) {
          if (data.type === 'onPlayerReady') {
            setIsReady(true);
            sendPostMessage(isActuallyMuted ? 'mute' : 'unMute');
            if (isActive) {
              sendPostMessage('play');
              setIsPlaying(true);
            }
          } else if (data.type === 'onCurrentTime') {
            const time = typeof data.value === 'object' ? data.value?.currentTime : data.value;
            if (typeof time === 'number' && time > 0) {
              setCurrentTime(time);
              setPlaybackStalled(false);
            }
          } else if (data.type === 'onStateChange') {
            // 1: PLAYING, 2: PAUSED, 0: ENDED
            if (data.value === 1) {
              setIsPlaying(true);
              setPlaybackStalled(false);
            } else if (data.value === 2 || data.value === 0) {
              setIsPlaying(false);
            }
          } else if (data.type === 'onPlayerError') {
            // Playback error or autoplay error from TikTok API
            setPlaybackStalled(true);
          }
        }
      } catch (e) {
        // Not a valid JSON message, ignore
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isIframeEnabled, isActuallyMuted, isActive]);

  // Watchdog timer: If active for 4.5s but playback never progressed, flag stalled
  useEffect(() => {
    if (!isIframeEnabled || !isActive) {
      setPlaybackStalled(false);
      return;
    }

    const timer = setTimeout(() => {
      if (currentTime === 0) {
        setPlaybackStalled(true);
      }
    }, 4500);

    return () => clearTimeout(timer);
  }, [isIframeEnabled, isActive, currentTime]);

  const handleIframeLoad = () => {
    setIsReady(true);
    setTimeout(() => {
      sendPostMessage(isActuallyMuted ? 'mute' : 'unMute');
      if (isActive) {
        sendPostMessage('play');
        setIsPlaying(true);
      }
    }, 400);
  };

  const embedUrl = `https://www.tiktok.com/player/v1/${videoId}?autoplay=1&muted=1&loop=1&controls=1&play_button=1&volume_control=0&music_info=0&description=0`;

  const getDirectTikTokUrl = (url?: string, id?: string) => {
    if (url) {
      const oembedMatch = url.match(/[?&]url=([^&]+)/);
      const rawUrl = oembedMatch ? decodeURIComponent(oembedMatch[1]) : url;
      if (rawUrl.includes('/video/')) {
        return rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
      }
    }
    return id ? `https://www.tiktok.com/@_/video/${id}` : 'https://www.tiktok.com';
  };

  const directTikTokUrl = getDirectTikTokUrl(videoUrl, videoId);

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-black flex items-center justify-center group"
    >
      {/* Poster image (always shown when not active / iframe not enabled, or before ready) */}
      {(!isIframeEnabled || !isReady) && poster ? (
        <img
          src={poster}
          alt=""
          className="absolute inset-0 w-full h-full object-cover z-0"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      ) : null}

      {/* Loading indicator when iframe is mounting but not ready */}
      {isIframeEnabled && !isReady ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
          <div className="w-10 h-10 border-3 border-white/20 border-t-white/80 rounded-full animate-spin" />
        </div>
      ) : null}

      {/* TikTok Player Iframe */}
      <div className="w-full h-full relative">
        {isIframeEnabled ? (
          <iframe
            ref={iframeRef}
            src={embedUrl}
            onLoad={handleIframeLoad}
            className="w-full h-full border-none"
            allow="autoplay; fullscreen; accelerometer; gyroscope; encrypted-media; picture-in-picture; web-share"
            title="TikTok Video Player"
            loading="lazy"
          />
        ) : null}
      </div>

      {/* Overlay UI — only show when iframe is NOT enabled (poster state) */}
      {!isIframeEnabled ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setIsIframeEnabled(true)}
            className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white backdrop-blur-md shadow-lg transition-transform hover:scale-105 cursor-pointer"
            aria-label="Putar video TikTok"
          >
            <Play className="w-7 h-7 ml-1 fill-white" />
          </button>
        </div>
      ) : null}

      {/* Fallback overlay when video stream is restricted/blocked by TikTok API (e.g. TikTok Shop affiliate posts) */}
      {playbackStalled && isActive ? (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center p-4 bg-black/60 backdrop-blur-sm pointer-events-auto text-center">
          {poster && (
            <img
              src={poster}
              alt=""
              className="absolute inset-0 w-full h-full object-cover -z-10 opacity-30"
            />
          )}
          <p className="text-white/90 text-xs md:text-sm font-medium mb-3 max-w-[220px]">
            Video ini terproteksi oleh TikTok Shop dan hanya dapat diputar langsung di TikTok.
          </p>
          <a
            href={directTikTokUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 border border-white/20 hover:bg-black text-white text-xs font-semibold rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 cursor-pointer"
          >
            <span>Buka di TikTok</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : null}

      {/* Brand watermark — pointer-events-none so it doesn't block iframe clicks */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
        <motion.div
          animate={{
            opacity: isActive ? 0.3 : 0.7,
            y: isActive ? 0 : -6
          }}
        >
          <h3 className="text-xl font-bold text-white tracking-tighter font-sans uppercase drop-shadow-md">secera</h3>
        </motion.div>
      </div>
    </div>
  );
}
